import { FC, ReactNode } from 'react';
import MuiLoadingButton from '@mui/lab/LoadingButton';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Icon from '../icon';
import { Theme } from '@mui/material';
import { IconType } from '../icon/helpers';

type ButtonType = 'ok' | 'submit' | 'cancel' | 'delete' | 'option';

type ButtonStyleProps = {
  color: 'primary' | 'error';
  variant: 'contained' | 'outlined';
  inputType: 'button' | 'submit';
};

export interface ButtonProps {
  label?: ReactNode;
  disabled?: boolean;
  size?: 'large' | 'medium' | 'small';
  type?: ButtonType;
  fullWidth?: boolean;
  className?: string;
  icon?: IconType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (event: any) => void; // @todo event type?
  loading?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    boxShadow: 'none',
    minWidth: 0,
    padding: '0 16px',
    fontSize: theme.typography.button.fontSize,
    '&:hover': {
      boxShadow: 'none',
    },
    '&.withIcon': {
      paddingLeft: 8,
      paddingRight: 12,
    },
    '&.MuiButton-outlined': {
      padding: '0 15px',
      backgroundColor: theme.palette.common.white,
      '&.withIcon': {
        paddingLeft: 7,
        paddingRight: 11,
      },
    },
    '&.MuiButton-sizeSmall': {
      height: 24,
    },
    '&.MuiButton-sizeMedium': {
      height: 32,
    },
    '&.MuiButton-sizeLarge': {
      height: 40,
    },
    '&.MuiButton-outlinedPrimary:hover': {
      backgroundColor: theme.palette.primary.light,
    },
    '&.MuiButton-contained, &.MuiButton-contained.Mui-disabled': {
      color: theme.palette.common.white,
    },
  },
  icon: {
    marginRight: 8,
  },
}));

const useStyleProps = (type: ButtonType): ButtonStyleProps => {
  switch (type) {
    case 'ok':
    default:
      return { color: 'primary', variant: 'contained', inputType: 'button' };
    case 'option':
      return { color: 'primary', variant: 'outlined', inputType: 'button' };
    case 'cancel':
      return { color: 'primary', variant: 'outlined', inputType: 'button' };
    case 'delete':
      return { color: 'error', variant: 'contained', inputType: 'button' };
    case 'submit':
      return { color: 'primary', variant: 'contained', inputType: 'button' };
  }
};

const Button: FC<ButtonProps> = ({
  label,
  disabled = false,
  size = 'medium',
  icon,
  fullWidth = false,
  className = '',
  onClick,
  type = 'ok',
  loading = false,
}) => {
  const styles = useStyles();
  const { color, variant, inputType } = useStyleProps(type);

  const hasTextLabel = !!label;
  const hasIcon = !!icon;
  const compClass = classNames(className, styles.button, {
    withIcon: hasIcon && hasTextLabel,
  });

  return (
    <MuiLoadingButton
      color={color}
      className={compClass}
      disabled={disabled}
      size={size}
      variant={variant}
      type={inputType}
      fullWidth={fullWidth}
      onClick={onClick}
      loading={loading}
    >
      {hasIcon && <Icon name={icon} className={hasTextLabel ? styles.icon : ''} size="small" />}
      {label}
    </MuiLoadingButton>
  );
};

export default Button;
